/**
 * MarketingHero — shared hero primitive for marketing surfaces.
 *
 * Used by:
 *   - `/handoff/demo` (issue #435) — basic shape (headline + subhead + CTA).
 *   - `/upgrade` paywall (issue #434) — eyebrow + secondary CTA shapes.
 *
 * Why a shared primitive?
 *   The two surfaces need the same visual language: brand monogram
 *   on top, a tight headline + subhead pair, and a single primary CTA.
 *   Forging the markup twice (once per surface) drifted in copy
 *   spacing, gradient stops, and CTA alignment within a couple of
 *   iterations, so we extracted this primitive in #435. Each surface
 *   supplies its own copy + CTA — this component only owns the
 *   layout + typography + gradient background.
 *
 * Implementation notes:
 *   - Pure presentational; no router / analytics imports.
 *   - The eyebrow, secondary CTA, and children are all optional so
 *     the simplest call site (`<MarketingHero headline="..." />`)
 *     still works.
 *   - Uses the existing `.paywall-hero` CSS class so the look stays
 *     consistent with the paywall surface — the paywall is the
 *     canonical hero on the web tier.
 */
import type { ReactElement, ReactNode } from 'react';
import { BrandMonogram } from '../lib/effectiveslp/BrandMonogram';

export interface MarketingHeroCta {
  label: string;
  onClick: () => void;
}

export interface MarketingHeroSecondaryCta {
  label: string;
  href: string;
}

export interface MarketingHeroProps {
  eyebrow?: string;
  headline: string;
  subhead?: string;
  cta?: MarketingHeroCta;
  secondaryCta?: MarketingHeroSecondaryCta;
  /** Optional slot rendered below the CTAs (e.g. social proof line). */
  children?: ReactNode;
  /**
   * Test id for the hero's primary CTA. Surfaces that already own
   * a test id for their CTA (the paywall uses `paywall-cta`) can
   * keep theirs; surfaces without a stable id (the demo landing)
   * use the default `marketing-hero-cta`.
   */
  ctaTestId?: string;
}

export function MarketingHero({
  eyebrow,
  headline,
  subhead,
  cta,
  secondaryCta,
  children,
  ctaTestId = 'marketing-hero-cta',
}: MarketingHeroProps): ReactElement {
  return (
    <section className="paywall-hero" aria-label={headline}>
      <BrandMonogram size={96} />
      {eyebrow ? <p className="paywall-hero-social-proof">{eyebrow}</p> : null}
      <h1 className="paywall-hero-headline">{headline}</h1>
      {subhead ? <p className="paywall-hero-subhead">{subhead}</p> : null}
      {cta ? (
        <button
          type="button"
          className="snap-button marketing-hero-cta"
          onClick={cta.onClick}
          data-testid={ctaTestId}
        >
          {cta.label}
        </button>
      ) : null}
      {secondaryCta ? (
        <a
          className="link-button marketing-hero-secondary-cta"
          href={secondaryCta.href}
        >
          {secondaryCta.label}
        </a>
      ) : null}
      {children}
    </section>
  );
}

export default MarketingHero;